import { Row, Rows } from 'common/types/scalarSpreadsheet';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTION_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TYPES,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MARKET_ADJUSTMENT_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEAN_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEDIAN_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_A_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_B_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SPECIFIED_ADJUSTMENT_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLES_CLASSNAME,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_WEIGHTED_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE,
} from 'pages/FundInvestments/common/constants';
import { CreateRowsParams } from './createRows.types';
import { createCalculationRow, createCompanyIndexRows } from './utils';

const { public_company: PUBLIC_COMPANY, index_etf: INDEX_ETF } =
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TYPES;

const createRows = (params: CreateRowsParams): Rows => {
  const { companyTypesPresence, isSingleComparison, marketCurrency, uniqueComparisons } = params;
  const { hasIndexEtf, hasPublicCompany } = companyTypesPresence;

  const titleAndDateRows: Rows = [
    {
      alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE_ALIAS,
      className: `${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME}__title read-only--white no-side-border division-bottom-only`,
      gridType: 'string',
      largeHeader: true,
      readOnly: true,
      value: FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE,
      colSpan: Object.keys(FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS).length - 1,
    } as Row,
    {
      alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES_ALIAS,
      className: `${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME}__dates read-only--white no-side-border`,
      gridType: 'string',
      readOnly: true,
      value: '',
    } as Row,
  ];

  const publicCompanyRows: Rows = hasPublicCompany
    ? [
        {
          alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE_ALIAS,
          className: `${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME}__company-title ${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLES_CLASSNAME}`,
          gridType: 'string',
          readOnly: true,
          value: PUBLIC_COMPANY.title,
        } as Row,
        ...createCompanyIndexRows({ displayOnlyIndexes: false, marketCurrency, uniqueComparisons }),
    ]
    : [];

  const indexEtfRows: Rows = hasIndexEtf
    ? [
        {
          alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE_ALIAS,
          className: `${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME}__index-title read-only--white ${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLES_CLASSNAME}`,
          gridType: 'string',
          readOnly: true,
          value: INDEX_ETF.title,
        } as Row,
        ...createCompanyIndexRows({ displayOnlyIndexes: true, marketCurrency, uniqueComparisons }),
    ]
    : [];

  const calculationsRows: Rows = [
    createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SPECIFIED_ADJUSTMENT_ALIAS }),
    createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTION_ALIAS }),
    createCalculationRow({
      alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MARKET_ADJUSTMENT_ALIAS,
      isSubtitle: true,
    }),
  ];

  // If there is only one comparison, we don't show the calculation rows
  if (!isSingleComparison) {
    calculationsRows.unshift(
      createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_WEIGHTED_ALIAS }),
      createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEDIAN_ALIAS }),
      createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEAN_ALIAS }),
      createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_A_ALIAS }),
      createCalculationRow({ alias: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_B_ALIAS })
    );
  }

  return [...titleAndDateRows, ...publicCompanyRows, ...indexEtfRows, ...calculationsRows];
};

export default createRows;
